import { portalFetch } from "@/lib/portalApi";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type GoogleConnectionStatus = {
  connected: boolean;
  email: string | null;
  status: "active" | "expired" | null;
  connectedAt: string | null;
};

type SyncLogEntry = {
  id: string;
  createdAt: string;
  action: string;
  provider: string;
  userEmail: string;
  status: string;
  detail: string | null;
  triggeredBy: string;
  dashboardTitle: string;
};

export function useGoogleConnection(tenantId: string | null) {
  return useQuery({
    queryKey: ["portal-google-connection", tenantId],
    queryFn: () =>
      portalFetch<GoogleConnectionStatus>(
        `/api/portal/google/connection/status?tenantId=${tenantId}`,
      ),
    enabled: !!tenantId,
  });
}

export function useGoogleDisconnect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      portalFetch<{ success: true }>("/api/portal/google/connection/disconnect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId }),
      }),
    onSuccess: (_data, tenantId) => {
      void queryClient.invalidateQueries({ queryKey: ["portal-google-connection", tenantId] });
    },
  });
}

export type SyncLogStatusFilter = "all" | "success" | "skipped" | "failed";

export type SyncLogPage = {
  entries: SyncLogEntry[];
  nextCursor: string | null;
};

/**
 * Paged view of the permission-sync log, newest first. Infinite rather than a fixed window because
 * a busy tenant writes dozens of rows per action, so a single page can cover less than an hour —
 * which is how SIM-3141's real evidence ended up unreachable below the fold.
 */
export function useGoogleSyncLog(
  tenantId: string | null,
  status: SyncLogStatusFilter = "all",
  limit = 50,
) {
  return useInfiniteQuery({
    queryKey: ["portal-google-sync-log", tenantId, status, limit],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ tenantId: tenantId ?? "", limit: String(limit) });
      if (pageParam) params.set("cursor", pageParam);
      if (status !== "all") params.set("status", status);
      return portalFetch<SyncLogPage>(`/api/portal/google/sync-log?${params.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!tenantId,
  });
}
